import { CultureInfoFormatter, ICoreContext, IDateFormatOptions } from '@msdyn365-commerce/core';
import { DateLocale } from '../actions/user-preferences/get-localization.action';
import { LOCALIZATION_DEFAULT_LOCALE } from '../configuration/localization';
import { ILocalizationState } from '../states/localization-state';

/**
 * Formats date.
 * @param context - Core context.
 * @param date - Date to display.
 * @returns String value.
 */
// Example usage: formatDate(this.props.context, new Date(CreatedDate)

export const formatDateLocale = (context: ICoreContext, date: Date | undefined) => {
    if (date === undefined) {
        return '';
    }

    const dateOptions: IDateFormatOptions = { year: 'numeric', month: 'numeric', day: 'numeric' };
    return context.cultureFormatter.formatDate(date, dateOptions);
};

export const dateLocaleFormatter = (localizationState: ILocalizationState) => {
    if (localizationState === undefined) {
        return new CultureInfoFormatter(LOCALIZATION_DEFAULT_LOCALE, 'EUR');
    }

    return new CultureInfoFormatter(localizationState.dateLocale, 'EUR');
};

export const checkIfValidDate = (date: string | undefined): boolean => {
    if (date && !isNaN(new Date(date).getTime())) {
        return true;
    } else {
        return false;
    }
};

export const toLocalDate = (input: string | undefined | Date): Date | string => {
    if (!input) {
        return '';
    }

    if (input instanceof Date) {
        if (isNaN(input.getTime())) {
            return '';
        }
        return input;
    }
    if (typeof input === 'string') {
        const match = input.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (!match) {
            return '';
        }
        const [, year, month, day] = match;
        return new Date(Number(year), Number(month) - 1, Number(day));
    }

    return '';
};

export const adjustTimezoneOffset = (date: Date) => {
    const now = new Date();

    // Get timezone offset in milliseconds
    const timezoneOffsetInMilliseconds = now.getTimezoneOffset() * 60000;

    // Set current time to the provided date
    date.setHours(now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds());

    // Adjust the date for timezone offset
    date.setTime(date.getTime() - timezoneOffsetInMilliseconds);

    return date;
};

export const isSameDay = (oldDate: Date, newDate: Date) => {
    return (
        oldDate.getFullYear() === newDate.getFullYear() &&
        oldDate.getMonth() === newDate.getMonth() &&
        oldDate.getDate() === newDate.getDate()
    );
};

export const getTomorrowDate = (): Date => {
    const tomorrow = new Date(+new Date() + 86400000);
    return tomorrow;
};

const splitDateString = (date: string, locale: string = DateLocale.US): { day: number; month: number; year: number } => {
    let day: number, month: number, year: number;
    if (locale === DateLocale.GB) {
        [day, month, year] = date.split('/').map(Number);
    } else if (locale === DateLocale.US) {
        [month, day, year] = date.split('/').map(Number);
    } else if (locale === DateLocale.DE) {
        [day, month, year] = date.split('.').map(Number);
    } else {
        [month, day, year] = date.split('/').map(Number);
    }

    return { day, month, year };
};

export const parseDateStringToDate = (dateString: string, locale: string): Date | undefined => {
    if (!dateString) {
        return undefined;
    }

    const { day, month, year } = splitDateString(dateString, locale);
    const date = new Date(year, month - 1, day);
    return isNaN(date.getTime()) ? undefined : date;
};

export const formatShortDate = (dateString: string, locale: string): string => {
    if (!dateString) {
        return '';
    }
    try {
        return new Date(dateString).toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
        return dateString;
    }
};

export const getDateObject = (requestedDate: string, locale?: string): string => {
    if (requestedDate !== '') {
        const { day, month, year } = splitDateString(requestedDate, locale);
        const pad = (num: number) => num.toString().padStart(2, '0');
        return `${year}-${pad(month)}-${pad(day)}`;
    } else {
        return '';
    }
};
